import { useCallback, useEffect, useState } from 'react';
import { notify } from '@/core/toast/notify';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { deskApi } from '@/api';
import { CustomerSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { usePagination } from '@/hooks/usePagination';
import { useDebounce } from '@/hooks/useDebounce';
import type { DeskTicket, DeskTicketRequest } from '@/types/desk';
import { showApiError } from '@/utils/apiError';

export function DeskTicketsPage() {
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<DeskTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<DeskTicketRequest>({ subject: '', source: 'MANUAL' });
  const [customerId, setCustomerId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await deskApi.listTickets({ page: pagination.page, size: pagination.size, search: debouncedSearch || undefined });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [pagination, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const columns: Column<DeskTicket>[] = [
    {
      key: 'ticketNumber',
      header: 'Ticket',
      render: (row) => (
        <Link to={`/desk/tickets/${row.id}`} className="text-primary-600 hover:underline font-medium">
          {row.ticketNumber}
        </Link>
      ),
    },
    { key: 'subject', header: 'Subject' },
    { key: 'source', header: 'Source' },
    { key: 'assignedEmployeeId', header: 'Assignee' },
  ];

  const create = async () => {
    setSaving(true);
    try {
      await deskApi.createTicket({
        ...form,
        customerId: customerId ? Number(customerId) : undefined,
      });
      notify.success('Ticket created');
      setOpen(false);
      setForm({ subject: '', source: 'MANUAL' });
      setCustomerId('');
      load();
    } catch (err) {
      showApiError(err, 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Tickets"
        description="Omnichannel support tickets with configurable workflows"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Desk', to: '/desk' }, { label: 'Tickets' }]}
        actions={<Button onClick={() => setOpen(true)}><AddIcon fontSize="small" /> New ticket</Button>}
      />
      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => { pagination.setSearch(v); pagination.resetPage(); }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
      />
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Create ticket"
        footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} loading={saving}>Create</Button></>}
      >
        <div className="grid gap-4">
          <Input label="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <Input label="Description" value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <CustomerSelect value={customerId} onChange={setCustomerId} />
        </div>
      </Modal>
    </div>
  );
}
